import { useLayoutEffect, useState, type ReactNode } from 'react'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import WifiOffRoundedIcon from '@mui/icons-material/WifiOffRounded'
import { Button } from './Button'
import { InteractiveConstellation } from './InteractiveConstellation'
import { getNlsString } from '../../nls/nlsRuntime'
import {
  installApiFailureCapture,
  installUnhandledApiFailureCapture,
  subscribeApiNetworkFailures,
  type ApiNetworkFailureDetail,
} from '../../services/apiConnectivity'

type OverlayProps = {
  failure: ApiNetworkFailureDetail | null
  onDismiss: () => void
}

export const ApiConnectivityOverlay = ({ failure, onDismiss }: OverlayProps) => {
  if (!failure) return null

  return (
    <section
      className="apiConnectivityOverlay"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="apiConnectivityTitle"
      aria-describedby="apiConnectivityDescription"
    >
      <InteractiveConstellation className="apiConnectivityOverlay__stars" density={1150} linkDistance={150} opacity={0.72} />
      <div className="apiConnectivityOverlay__panel">
        <div className="apiConnectivityOverlay__signal" aria-hidden="true">
          <WifiOffRoundedIcon fontSize="inherit" />
        </div>
        <div className="apiConnectivityOverlay__copy">
          <span className="apiConnectivityOverlay__eyebrow">{getNlsString('NLSCommonConnectivityEyebrow')}</span>
          <h1 id="apiConnectivityTitle">{getNlsString('NLSCommonConnectivityTitle')}</h1>
          <p id="apiConnectivityDescription">
            {getNlsString('NLSCommonConnectivityDescription')}
          </p>
        </div>
        <div className="apiConnectivityOverlay__status" aria-label={getNlsString('NLSCommonAriaConnectionStatus')}>
          <span />
          <span>{getNlsString('NLSCommonConnectivityRequestPaused')}</span>
          <span>{failure.method ?? getNlsString('NLSCommonStatusApi')}</span>
        </div>
        <div className="apiConnectivityOverlay__actions">
          <Button variant="primary" size="md" icon={<CloseRoundedIcon fontSize="inherit" />} onClick={onDismiss}>
            {getNlsString('NLSCommonActionBackToWorkspace')}
          </Button>
        </div>
      </div>
    </section>
  )
}

export const ApiConnectivityProvider = ({ children }: { children: ReactNode }) => {
  const [failure, setFailure] = useState<ApiNetworkFailureDetail | null>(null)

  useLayoutEffect(() => {
    const uninstallFetchCapture = installApiFailureCapture()
    const uninstallUnhandledCapture = installUnhandledApiFailureCapture()
    const unsubscribeFailures = subscribeApiNetworkFailures(setFailure)
    return () => {
      unsubscribeFailures()
      uninstallUnhandledCapture()
      uninstallFetchCapture()
    }
  }, [])

  return (
    <>
      {children}
      <ApiConnectivityOverlay failure={failure} onDismiss={() => setFailure(null)} />
    </>
  )
}
